/**
 * Pull-test installation drive times are stored as seconds per foot on the API
 * (`installation.fields.driveTimeSecByFoot`: `number[]`). Values support hundredths
 * of a second (0.01 s resolution).
 */

/** Round a drive-time value to hundredths of a second. */
export function roundPullTestDriveTimeSec(sec: number): number {
  if (!Number.isFinite(sec) || sec < 0) {
    return 0;
  }
  return Math.round(sec * 100) / 100;
}

/** Format one drive-time segment for UI display (always two decimal places). */
export function formatPullTestDriveTimeSec(sec: number): string {
  return roundPullTestDriveTimeSec(sec).toFixed(2);
}

/** Comma-separated drive-time list for install form storage / timer `displayValue`. */
export function formatPullTestDriveTimeSecList(values: readonly number[]): string {
  return values.map(v => formatPullTestDriveTimeSec(v)).join(', ');
}

/**
 * Parses comma / semicolon / whitespace separated seconds-per-foot entries.
 * Returns `null` when any token is missing, non-numeric, or negative.
 */
export function parsePullTestDriveTimeSecList(raw: string): number[] | null {
  const parts = String(raw)
    .split(/[,;\s]+/)
    .map(s => s.trim())
    .filter(Boolean);
  if (parts.length === 0) {
    return null;
  }
  const out: number[] = [];
  for (const p of parts) {
    const n = Number(p);
    if (!Number.isFinite(n) || n < 0) {
      return null;
    }
    out.push(roundPullTestDriveTimeSec(n));
  }
  return out;
}

/** One foot row in the install drive-time table with a segment past planned embedment. */
export interface PullTestDriveTimePastEmbedmentFoot {
  footNumber: number;
  driveTimeSec: number;
}

/**
 * Feet with recorded drive times past the assigned embedment foot cap — same rule as the
 * install timer red-X rows (`Math.max(1, Math.round(assignedEmbedmentDepthFt))`).
 */
export function pullTestDriveTimeFeetPastEmbedment(
  driveTimeSecByFoot: readonly number[],
  embedmentDepthFt: number | null | undefined
): PullTestDriveTimePastEmbedmentFoot[] {
  if (!Array.isArray(driveTimeSecByFoot) || driveTimeSecByFoot.length === 0) {
    return [];
  }
  if (typeof embedmentDepthFt !== 'number' || !Number.isFinite(embedmentDepthFt) || embedmentDepthFt <= 0) {
    return [];
  }
  const cap = Math.max(1, Math.round(embedmentDepthFt));
  const out: PullTestDriveTimePastEmbedmentFoot[] = [];
  for (let i = 0; i < driveTimeSecByFoot.length; i++) {
    const footNumber = i + 1;
    if (footNumber <= cap) {
      continue;
    }
    const sec = driveTimeSecByFoot[i];
    if (typeof sec !== 'number' || !Number.isFinite(sec)) {
      continue;
    }
    out.push({ footNumber, driveTimeSec: roundPullTestDriveTimeSec(sec) });
  }
  return out;
}

/** True when recorded actual embedment exceeds the piling's assigned (design) depth. */
export function pullTestActualEmbedmentPastAssigned(
  actualEmbedmentDepthFt: number | null | undefined,
  assignedEmbedmentDepthFt: number | null | undefined
): boolean {
  if (
    typeof assignedEmbedmentDepthFt !== 'number' ||
    !Number.isFinite(assignedEmbedmentDepthFt) ||
    assignedEmbedmentDepthFt <= 0
  ) {
    return false;
  }
  if (typeof actualEmbedmentDepthFt !== 'number' || !Number.isFinite(actualEmbedmentDepthFt)) {
    return false;
  }
  return actualEmbedmentDepthFt > assignedEmbedmentDepthFt;
}

/** Rounded foot cap from assigned embedment depth (`Math.max(1, Math.round(ft))`). */
export function pullTestAssignedEmbedmentFootCap(
  assignedEmbedmentDepthFt: number | null | undefined
): number | null {
  if (
    typeof assignedEmbedmentDepthFt !== 'number' ||
    !Number.isFinite(assignedEmbedmentDepthFt) ||
    assignedEmbedmentDepthFt <= 0
  ) {
    return null;
  }
  return Math.max(1, Math.round(assignedEmbedmentDepthFt));
}
